import { z } from 'zod';
import { DISCIPLINE_ORDER } from '../../constants/disciplines';

// Section keys are the contract that ties THREE places together. If you add /
// rename / remove a key here, mirror it in:
//   - backend/app/schemas.py — ThirtyDaySummaryCommunicationDataPayload
//     (extra='forbid', so a stray key → 422 on PATCH)
//   - backend/app/services/summary_service.py —
//     SHARED_30D_SUMMARY_CLINICAL_SECTIONS / THIRTY_DAY_SUMMARY_CLINICAL_SECTIONS
//     (the (key, label) tuples the PDF renders)
// Keep all three in sync or the PDF render / PATCH validation will drift.
// The Axxess 30-day form has mixed field kinds (not textarea-only), so SectionDef
// carries a `kind` discriminator and the schema builder branches per kind. THIRTY_DAY_SUMMARY_BASE_SECTIONS is
// the reusable base for a future 60-day config ([...BASE, ...SIXTY_DAY_EXTRA]).

export type FieldKind =
  | 'textarea'
  | 'checkbox-group'
  | 'single-select'
  | 'vitals-grid';

export type CheckboxGroupLayout = 'wrap' | 'inline';

export type SectionDef = {
  key: string;
  label: string;
  kind: FieldKind;
  options?: string[];
  allowOther?: boolean;
  /** checkbox-group only: inline for short labels (SN, PT, …), wrap for long ones. */
  checkboxLayout?: CheckboxGroupLayout;
  /** checkbox-group wrap layout: cap column count (e.g. 3 for Homebound Status). */
  checkboxMaxColumns?: number;
  placeholder?: string;
};

// The 8 Axxess homebound-status options (checkbox group + free-text Other).
const HOMEBOUND_STATUS_OPTIONS = [
  'N/A',
  'Exhibits considerable & taxing effort to leave home',
  'Requires assistance of another to get up & moving safely',
  'Severe Dyspnea',
  'Unable to safely leave home unassisted',
  'Unsafe to leave due to cognitive/psychiatric impairments',
  'Unable to leave home due to medical restriction(s)',
  'Other',
];

const PATIENT_CONDITION_OPTIONS = [
  'Stable',
  'Improved',
  'Unchanged',
  'Unstable',
  'Declined',
];

const SERVICE_OPTIONS = [...DISCIPLINE_ORDER, 'Other'];

// Discriminator written into communication_data on save and validated by the
// schema below — single source of truth so the write payload and the zod schema
// can't drift (mirrors the backend `ThirtyDaySummaryCommunicationDataPayload`).
export const THIRTY_DAY_SUMMARY_PAYLOAD_KIND = 'thirty_day_summary';

// Vitals-grid layout: 2 rows × 8 numeric-string columns. Cell keys / row labels
// match `_VITALS_GRID_COLUMNS` in app/services/summary_service.py.
export const VITALS_GRID_ROWS = ['Lowest', 'Highest'] as const;
export const VITALS_GRID_COLUMNS: { key: string; label: string }[] = [
  { key: 'bp_sys', label: 'BP Sys' },
  { key: 'bp_dia', label: 'BP Dia' },
  { key: 'hr', label: 'HR' },
  { key: 'resp', label: 'Resp' },
  { key: 'temp', label: 'Temp' },
  { key: 'weight', label: 'Weight' },
  { key: 'bs', label: 'BS' },
  { key: 'pain', label: 'Pain' },
];

/**
 * Reusable base sections for all interval summaries (30-day uses them verbatim;
 * a future 60-day appends its extras). Order matches the backend section tuples.
 */
export const THIRTY_DAY_SUMMARY_BASE_SECTIONS: SectionDef[] = [
  {
    key: 'homebound_status',
    label: 'Homebound Status',
    kind: 'checkbox-group',
    options: HOMEBOUND_STATUS_OPTIONS,
    checkboxMaxColumns: 2,
    allowOther: true,
  },
  {
    key: 'patient_condition',
    label: 'Patient Condition',
    kind: 'single-select',
    options: PATIENT_CONDITION_OPTIONS,
  },
  {
    key: 'services_provided',
    label: 'Service(s) Provided',
    kind: 'checkbox-group',
    options: SERVICE_OPTIONS,
    checkboxLayout: 'inline',
  },
  {
    key: 'vital_sign_ranges',
    label: 'Vital Sign Ranges',
    kind: 'vitals-grid',
  },
  {
    key: 'summary_of_care_provided',
    label: 'Summary of Care Provided',
    kind: 'textarea',
  },
  {
    key: 'current_condition',
    label: "Patient's Current Condition",
    kind: 'textarea',
  },
  {
    key: 'goals',
    label: 'Goals',
    kind: 'textarea',
  },
  {
    key: 'recommended_services',
    label: 'Recommended Services',
    kind: 'checkbox-group',
    options: SERVICE_OPTIONS,
    checkboxLayout: 'inline',
    allowOther: true,
  },
];

type SchemaShape = Record<string, z.ZodTypeAny>;

let cachedSections: SectionDef[] | undefined;
let cachedSchema: z.ZodObject<SchemaShape> | undefined;

export function getThirtyDaySummarySections(): SectionDef[] {
  if (!cachedSections) {
    cachedSections = THIRTY_DAY_SUMMARY_BASE_SECTIONS;
  }
  return cachedSections;
}

// One vitals row: each of the 8 cells is an optional numeric string.
const vitalsRowSchema = z.object(
  Object.fromEntries(
    VITALS_GRID_COLUMNS.map(col => [col.key, z.string().optional()])
  ) as SchemaShape
);

function buildSchema(): z.ZodObject<SchemaShape> {
  const shape: SchemaShape = {
    payload_kind: z.literal(THIRTY_DAY_SUMMARY_PAYLOAD_KIND).optional(),
  };
  for (const section of getThirtyDaySummarySections()) {
    switch (section.kind) {
      case 'textarea':
      case 'single-select':
        shape[section.key] = z.string().optional();
        break;
      case 'checkbox-group':
        shape[section.key] = z.array(z.string()).optional();
        if (section.allowOther) {
          shape[`${section.key}_other`] = z.string().optional();
        }
        break;
      case 'vitals-grid':
        shape[section.key] = z
          .object(
            Object.fromEntries(
              VITALS_GRID_ROWS.map(row => [row, vitalsRowSchema.optional()])
            ) as SchemaShape
          )
          .optional();
        break;
    }
  }
  return z.object(shape);
}

export function getThirtyDaySummarySchema(): z.ZodObject<SchemaShape> {
  if (!cachedSchema) {
    cachedSchema = buildSchema();
  }
  return cachedSchema;
}
